import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NOVEDADES_DURACION, NOVEDADES_ID, NOVEDADES_MINIATURA, NOVEDADES_POSTER, NOVEDADES_VIDEO_URL, tocaPopupNovedades } from '@alexiarest/shared';
import { useMarkAvisoVisto, useMe } from '../../api/hooks';
import './novedades.css';

/**
 * «Alexia tiene nueva cara» (lanzamiento, 18 oct 2026): el pop-up de Alex
 * (popup.html, clases `.alx-nov`), con sus textos tal cual.
 *
 * - Sale UNA vez por persona: «Entendido» (o la X, Esc, tocar fuera) lo
 *   guarda en la membresía (`POST /v1/me/avisos`, id `novedades-2026-10`).
 *   Si eso falla, la pestaña se acuerda y no se repite en la sesión.
 * - Solo a quien tenía cuenta antes del corte (`user.novedades`, regla en
 *   shared/novedades.ts). Los que se dan de alta después ya ven lo nuevo.
 * - Vive en el Layout, así que nunca sale en la cocina pública, /descargar,
 *   el alta, «Elige tu plan» ni el cuestionario. Si está abierto el aviso
 *   de «elige un plan», espera a que se cierre.
 * - Desde Ayuda › Novedades se vuelve a abrir (`abrirNovedades`).
 */

const CLAVE_SESION = 'alexia:novedades-visto';
const EVENTO_ABRIR = 'alexia:abrir-novedades';

/** Ayuda › Novedades: lo vuelve a abrir aunque ya se haya visto. */
export function abrirNovedades(): void {
  window.dispatchEvent(new Event(EVENTO_ABRIR));
}

function vistoEnLaSesion(): boolean {
  try { return sessionStorage.getItem(CLAVE_SESION) === '1'; } catch { return false; }
}
function recordarEnLaSesion(): void {
  try { sessionStorage.setItem(CLAVE_SESION, '1'); } catch { /* sin almacenamiento: queda la API */ }
}

/** Algo que no se debe tapar: el aviso de «elige un plan» u otro diálogo modal abierto. */
function hayOtroModal(): boolean {
  return Boolean(document.querySelector('.sin-plan-modal, [role="dialog"][aria-modal="true"]:not(.alx-nov)'));
}

export function Novedades(): JSX.Element | null {
  const me = useMe();
  const marcar = useMarkAvisoVisto();
  const location = useLocation();
  const [abierto, setAbierto] = useState(false);
  const automatico = useRef(false);
  const user = me.data?.user;
  const toca = Boolean(user?.novedades) && tocaPopupNovedades({ avisosVistos: user?.avisosVistos }) && !vistoEnLaSesion();

  // Sale solo una vez, cuando nada lo tapa.
  useEffect(() => {
    if (!toca || automatico.current) return undefined;
    let vivo = true;
    const probar = () => {
      if (!vivo || automatico.current) return;
      if (hayOtroModal()) return;
      automatico.current = true;
      setAbierto(true);
    };
    const t0 = window.setTimeout(probar, 600);
    const iv = window.setInterval(probar, 1000);
    return () => { vivo = false; window.clearTimeout(t0); window.clearInterval(iv); };
  }, [toca, location.pathname]);

  useEffect(() => {
    const abrir = () => setAbierto(true);
    window.addEventListener(EVENTO_ABRIR, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR, abrir);
  }, []);

  const cerrar = () => {
    setAbierto(false);
    recordarEnLaSesion();
    if (!(user?.avisosVistos ?? []).includes(NOVEDADES_ID)) marcar.mutate(NOVEDADES_ID);
  };

  if (!abierto) return null;
  return <PopupNovedades onCerrar={cerrar} />;
}

export function PopupNovedades({ onCerrar }: { onCerrar: () => void }): JSX.Element {
  const raiz = useRef<HTMLDivElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  const entendido = useRef<HTMLButtonElement>(null);
  const [video, setVideo] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const antes = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => { setVisible(true); entendido.current?.focus({ preventScroll: true }); }, 20);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = overflow;
      antes?.focus?.();
    };
  }, []);

  // Esc cierra; Tab no sale del pop-up.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCerrar(); return; }
      if (e.key !== 'Tab' || !raiz.current) return;
      const focos = [...raiz.current.querySelectorAll<HTMLElement>('button, [href], video, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.hasAttribute('disabled'));
      if (focos.length === 0) return;
      const primero = focos[0];
      const ultimo = focos[focos.length - 1];
      const dentro = raiz.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === primero || !dentro)) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && (document.activeElement === ultimo || !dentro)) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', tecla, true);
    return () => document.removeEventListener('keydown', tecla, true);
  }, [onCerrar]);

  const verVideo = () => {
    setVideo(true);
    window.setTimeout(() => caja.current?.scrollIntoView({ block: 'nearest' }), 50);
  };

  return (
    <div
      ref={raiz}
      className={`alx-nov${visible ? ' is-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alx-nov-title"
      data-sin-plan="libre"
      onClick={(e) => { if (e.target === e.currentTarget) onCerrar(); }}
    >
      <div className="alx-nov__card">
        <div className="alx-nov__head">
          <div>
            <span className="alx-nov__tag">Novedades</span>
            <h2 className="alx-nov__title" id="alx-nov-title">Alexia tiene nueva cara</h2>
            <p className="alx-nov__lead"><b>Tus datos están todos igual: no se ha perdido nada.</b> Cambió cómo se ve y dónde está cada cosa.</p>
          </div>
          <button type="button" className="alx-nov__x" onClick={onCerrar} aria-label="Cerrar">×</button>
        </div>

        <div className="alx-nov__grid">
          <div>
            <div className="alx-nov__video" ref={caja} style={video ? undefined : { backgroundImage: `url('${NOVEDADES_MINIATURA}')` }}>
              {video ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- los subtítulos van dentro del vídeo
                <video src={NOVEDADES_VIDEO_URL} poster={NOVEDADES_POSTER} controls autoPlay playsInline preload="auto" />
              ) : (
                <button type="button" className="alx-nov__video-btn" onClick={verVideo} aria-label={`Ver el vídeo de novedades (${NOVEDADES_DURACION})`} />
              )}
            </div>
            <h3 className="alx-nov__h">Lo nuevo</h3>
            <ul className="alx-nov__new">
              <li><span className="alx-nov__dot">1</span><span><b>Para hoy</b>: lo que tienes pendiente, como una lista que vas tachando.</span></li>
              <li><span className="alx-nov__dot">?</span><span><b>Un «?» junto a cada número</b>: tócalo y te explica qué es. EBITDA ahora se llama «Te dejó el negocio».</span></li>
              <li><span className="alx-nov__dot"><svg width="11" height="14" viewBox="0 0 11 14" aria-hidden="true"><rect x="1" y="1" width="9" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" /><circle cx="5.5" cy="10.6" r=".9" fill="currentColor" /></svg></span><span><b>Alexia en tu teléfono</b>: ya disponible en App Store y Google Play.</span></li>
            </ul>
          </div>
          <div>
            <h3 className="alx-nov__h">Dónde está ahora lo que ya usabas</h3>
            <ul className="alx-nov__map">
              <li><span className="alx-nov__old">Antes: Ingresos</span><span className="alx-nov__now"><span>Finanzas ›</span> Ventas</span></li>
              <li><span className="alx-nov__old">Antes: Cuenta de resultados</span><span className="alx-nov__now"><span>Finanzas ›</span> Estado de resultados</span></li>
              <li><span className="alx-nov__old">Antes: Recetas y costeo</span><span className="alx-nov__now"><span>Costeo ›</span> Tu carta <span>y</span> Preparaciones</span></li>
              <li><span className="alx-nov__old">Antes: Precios de la carta</span><span className="alx-nov__now"><span>Costeo ›</span> Tu carta</span></li>
              <li><span className="alx-nov__old">Antes: Inventario</span><span className="alx-nov__now"><span>Costeo ›</span> Recuento mensual</span></li>
              <li><span className="alx-nov__old">Antes: Ajustes</span><span className="alx-nov__now">Configuración <span>(abajo del menú)</span></span></li>
              <li><span className="alx-nov__old">Antes: Pendientes de confirmar</span><span className="alx-nov__now">Alexia <span>· te lo recuerda «Para hoy»</span></span></li>
            </ul>
          </div>
        </div>

        <div className="alx-nov__foot">
          <span className="alx-nov__hint">Lo vuelves a ver en Ayuda › Novedades</span>
          <div className="alx-nov__btns">
            <button type="button" className="alx-nov__btn" onClick={verVideo}>Ver el vídeo</button>
            <button type="button" className="alx-nov__btn alx-nov__btn--main" ref={entendido} onClick={onCerrar}>Entendido</button>
          </div>
        </div>
      </div>
    </div>
  );
}
